const MAX_INPUT_BYTES = 10 * 1024 * 1024;
const DEFAULT_MAX_DIMENSION = 1200;
const DEFAULT_TARGET_BYTES = 1.5 * 1024 * 1024;
const SKIP_COMPRESS_BELOW_BYTES = 400 * 1024;
const MIN_QUALITY = 0.5;
const START_QUALITY = 0.85;

function scaleDimensions(width, height, maxDimension) {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }
  const ratio = Math.min(maxDimension / width, maxDimension / height);
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to compress image"));
      },
      "image/jpeg",
      quality
    );
  });
}

/**
 * Compress a photo for upload: resize large images and encode as JPEG.
 * Small files and GIFs are returned unchanged.
 */
export async function compressImage(file, options = {}) {
  const {
    maxInputBytes = MAX_INPUT_BYTES,
    maxDimension = DEFAULT_MAX_DIMENSION,
    targetMaxBytes = DEFAULT_TARGET_BYTES,
  } = options;

  if (!file?.type?.startsWith("image/")) {
    throw new Error("Please choose a valid image file (JPG, PNG, WebP, etc.)");
  }

  if (file.size > maxInputBytes) {
    throw new Error(`Image must be ${maxInputBytes / (1024 * 1024)} MB or smaller`);
  }

  if (file.type === "image/gif" || file.size <= SKIP_COMPRESS_BELOW_BYTES) {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  const { width, height } = scaleDimensions(bitmap.width, bitmap.height, maxDimension);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close?.();
    return file;
  }

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  let quality = START_QUALITY;
  let blob = await canvasToBlob(canvas, quality);

  while (blob.size > targetMaxBytes && quality > MIN_QUALITY) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, quality);
  }

  const baseName = file.name.replace(/\.[^.]+$/, "") || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

export const IMAGE_UPLOAD_MAX_MB = MAX_INPUT_BYTES / (1024 * 1024);
